import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import getUTMParametersFromCookies from "utils/getUTMParametersFromCookies"
import * as Sentry from "@sentry/react"
import pushGTMEvent from "utils/pushGTMEvent"
import submitToNetlify from "utils/submitToNetlify"
import useHasMounted from "../hooks/useHasMounted"

const HubspotForm = ({
  children,
  formName,
  preSubmit,
  postSubmit,
  formValues,
  endpoint,
  fieldMapping,
  setSending,
  recaptchaValue,
  ...props
}) => {
  const [inNetlifyBuild, setInNetlifyBuild] = useState(true)
  const [honey, setHoney] = useState("")
  const hasMounted = useHasMounted()
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false)

  useEffect(() => {
    setInNetlifyBuild(false)
  }, [])

  const handleFormFocus = async () => {
    if (!isRecaptchaVerified && preSubmit) {
      const isValid = await preSubmit()
      if (isValid) {
        setIsRecaptchaVerified(true)
      }
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    pushGTMEvent("submit_button_clicked", formName)

    if (!isRecaptchaVerified) {
      console.error("ReCAPTCHA validation is failed.")
    }

    setSending(true)

    let userIp = ""
    try {
      const res = await fetch("https://api.ipify.org?format=json")
      if (!res.ok) {
        throw new Error("Unable to get IP")
      }
      const data = await res.json()
      userIp = data.ip || ""
    } catch (err) {
      console.error("Error when trying to get an IP", err)
    }

    const utmParameters = getUTMParametersFromCookies()
    const mergedFormValues = { ...formValues, ...utmParameters }

    if (mergedFormValues?.name) {
      const parts = mergedFormValues.name.trim().split(/\s+/)
      const firstName = parts.shift() || ""
      const lastName = parts.join(" ")
      mergedFormValues.name = firstName
      mergedFormValues.lastname = lastName
    }

    mergedFormValues.recaptchaValue = recaptchaValue || 0

    const hubspotData = [
      ...Object.keys(fieldMapping).map((key) => ({
        name: fieldMapping[key],
        value: mergedFormValues[key] || "",
      })),
      ...Object.keys(utmParameters).map((key) => ({
        name: key,
        value: utmParameters[key],
      })),
    ]

    const hutkMatch = document.cookie.match(/hubspotutk=([^;]*)/)?.[1]

    const context = {
      pageUri: window.location.href,
      pageName: document.title,
      ipAddress: userIp,
    }

    if (hutkMatch) {
      context.hutk = hutkMatch
    }

    const hubspotPayload = {
      fields: hubspotData,
      context,
    }

    await submitToNetlify(formName, mergedFormValues)

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(hubspotPayload),
      })

      setSending(false)

      if (response.ok) {
        console.log(`Form ${formName} submitted successfully.`)
        pushGTMEvent("submit_success", formName)
        if (postSubmit) postSubmit(true)
      } else {
        if (postSubmit) postSubmit(false)
        console.error(`HubSpot form submission failed: ${response.statusText}`)
        Sentry.captureException(
          new Error(
            `HubSpot form submission failed with status code, ${response.status}. Error type: ${response.type}. Url: ${response.url}. Response text: ${response.statusText}`,
          ),
        )
      }
    } catch (error) {
      if (postSubmit) postSubmit(false)
      Sentry.captureException(new Error(`HubSpot error submitting the form: ${error}`))
    }
  }

  if (inNetlifyBuild && !hasMounted) {
    return (
      <form
        {...props}
        name={formName}
        onFocus={handleFormFocus}
        onSubmit={onSubmit}
        data-netlify="true"
        netlify-honeypot="infoo"
      >
        {children}
        <input type="hidden" name="userID" value={formValues.userID || ""} />
        <input type="hidden" name="form-name" value={formName} />
      </form>
    )
  }

  return (
    <form {...props} onFocus={handleFormFocus} onSubmit={onSubmit}>
      {children}
      <input
        type="hidden"
        name="description"
        aria-label="description"
        value={honey}
        onChange={(e) => setHoney(e.target.value)}
      />
      <input type="hidden" name="userID" value={formValues.userID || ""} />
    </form>
  )
}

HubspotForm.propTypes = {
  formName: PropTypes.string.isRequired,
  formValues: PropTypes.objectOf(PropTypes.string).isRequired,
  children: PropTypes.node,
  preSubmit: PropTypes.func,
  setSending: PropTypes.func,
  postSubmit: PropTypes.func,
  fieldMapping: PropTypes.objectOf(PropTypes.string),
  endpoint: PropTypes.string.isRequired,
  recaptchaValue: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
}

HubspotForm.defaultProps = {
  children: null,
  preSubmit: () => true,
  postSubmit: () => {},
  setSending: () => null,
  fieldMapping: {},
  recaptchaValue: null,
}

export default HubspotForm
